import type { ReactNode } from "react";
import { Check, X } from "lucide-react";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { type MappableElement, elementLabel, mapped } from "./element-mapping";

/** One mapped element, as a chip: its number, its full wording, and a mark where the caller has one. */
export function ElementChip({
  element,
  onRemove,
  mark,
}: {
  /** An element with no number yet is one Praxis suggests, named as new. */
  element: Pick<MappableElement, "text"> & { number: number | null };
  onRemove?: () => void;
  /** What the page says of the element here, drawn after its words. */
  mark?: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-[11.5px]",
        onRemove === undefined
          ? "border-success-border bg-success-surface"
          : "border-border bg-muted",
      )}
    >
      {onRemove === undefined && (
        <Check className="h-3 w-3 flex-none text-success" aria-hidden />
      )}
      <b className="flex-none">
        {element.number === null ? "New element" : `Element ${element.number}`}
      </b>
      <span className="truncate text-muted-foreground">{element.text}</span>
      {mark}
      {onRemove !== undefined && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove element ${element.number}`}
          className="flex-none cursor-pointer rounded p-0.5 text-muted-foreground hover:text-destructive"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

/**
 * The mapping control: the chips already chosen, and a select of what is left.
 *
 * A native select, as the design draws it. Choosing adds; the chip's cross
 * removes. Zero, one or many elements are all fine.
 */
export function ElementPicker({
  elements,
  selected,
  onChange,
}: {
  elements: readonly MappableElement[];
  selected: readonly string[];
  onChange: (elementIds: string[]) => void;
}) {
  const chosen = mapped(selected, elements);
  const remaining = elements.filter((e) => !selected.includes(e.element_id));
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap gap-1.5">
        {chosen.length === 0 ? (
          <span className="text-[11.5px] text-muted-foreground">
            No elements yet. Optional.
          </span>
        ) : (
          chosen.map((element) => (
            <ElementChip
              key={element.element_id}
              element={element}
              onRemove={() =>
                onChange(selected.filter((id) => id !== element.element_id))
              }
            />
          ))
        )}
      </div>
      <Select
        size="dense"
        aria-label="Add an invention element"
        value=""
        disabled={remaining.length === 0}
        onChange={(event) => {
          if (event.target.value !== "")
            onChange([...selected, event.target.value]);
        }}
        className="w-fit max-w-full"
      >
        <option value="">＋ Add an invention element…</option>
        {remaining.map((element) => (
          <option key={element.element_id} value={element.element_id}>
            {elementLabel(element)}
          </option>
        ))}
      </Select>
    </div>
  );
}
